import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "wouter";
import { useApiData } from "@/hooks/useApiData";
import type { Project } from "@shared/types";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { ExternalLink, ArrowLeft, Layers, Cpu, Lightbulb } from "lucide-react";

function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [heroVisible, setHeroVisible] = useState(false);

  const { data: project, loading } = useApiData<Project>(`/projects/${id}`);

  useEffect(() => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() => setHeroVisible(true));
  }, []);

  useEffect(() => {
    if (!loading && !project) {
      setLocation("/projects");
    }
  }, [loading, project, setLocation]);

  if (loading || !project) return null;

  const sections = [
    { icon: Lightbulb, title: "על הפרויקט", content: project.about },
    { icon: Layers, title: "הפתרון שפיתחנו", content: project.solution },
    { icon: Cpu, title: "טכנולוגיה", content: project.technology },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#66CDAA]/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#66CDAA]/[0.03] rounded-full blur-[100px]" />
        </div>

        <div className="container relative">
          <div
            className={`transition-all duration-1000 ${
              heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <Link
              href="/projects"
              className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#66CDAA] transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4 rotate-180" />
              חזרה לפרויקטים
            </Link>

            <span className="inline-block px-3 py-1.5 rounded-full bg-[#66CDAA]/10 border border-[#66CDAA]/20 text-xs text-[#66CDAA] mb-4">
              {project.category}
            </span>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black font-[Rubik] text-white mb-6 leading-[1.1]">
              {project.name}
            </h1>
          </div>
        </div>
      </section>

      {/* Project Image */}
      <section className="pb-12 md:pb-16">
        <div className="container">
          <AnimatedSection>
            <div className="relative aspect-[2/1] rounded-2xl md:rounded-3xl overflow-hidden border border-white/[0.08] bg-gradient-to-br from-[#0B1120] via-[#0F172A] to-[#131D30]">
              {project.image ? (
                <img
                  src={project.image}
                  alt={project.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#0B1120] via-[#0F172A] to-[#131D30] relative">
                  <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                      backgroundImage: `linear-gradient(rgba(102,205,170,1) 1px, transparent 1px), linear-gradient(90deg, rgba(102,205,170,1) 1px, transparent 1px)`,
                      backgroundSize: "50px 50px",
                    }}
                  />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-4">
                    <div className="w-20 h-20 rounded-2xl border border-[#66CDAA]/15 bg-[#66CDAA]/[0.04] flex items-center justify-center">
                      <Layers className="w-9 h-9 text-[#66CDAA]/25" />
                    </div>
                    <span className="text-sm text-white/15">תמונת הפרויקט</span>
                  </div>
                </div>
              )}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Project Content */}
      <section className="pb-20 md:pb-28">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-4 mb-12">
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="glow-btn inline-flex items-center gap-2 text-base px-8 py-3.5"
              >
                <span>מעבר לאתר</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="space-y-6">
              {sections.map((section, i) => (
                <AnimatedSection key={i} delay={i * 150}>
                  <div className="relative p-6 md:p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-[#66CDAA]/15 hover:bg-white/[0.04] transition-all duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#66CDAA]/10 border border-[#66CDAA]/15 flex items-center justify-center shrink-0">
                        <section.icon className="w-5 h-5 text-[#66CDAA]" />
                      </div>
                      <h2 className="text-xl font-bold font-[Rubik] text-white">
                        {section.title}
                      </h2>
                    </div>
                    <p className="text-base text-white/55 leading-[1.8] pr-0 md:pr-12">
                      {section.content}
                    </p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#66CDAA]/[0.03] to-transparent" />
        <div className="container relative text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-[Rubik] text-white mb-4">
            רוצים פרויקט כזה?
          </h2>
          <p className="text-white/50 mb-8 max-w-lg mx-auto">
            בואו נדבר על הפרויקט הבא שלכם ונהפוך את החזון שלכם למציאות דיגיטלית.
          </p>
          <Link
            href="/contact"
            className="glow-btn inline-flex items-center gap-2 text-base px-8 py-3.5"
          >
            <span>דברו איתנו</span>
            <ArrowLeft className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
